/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { setDiff } from '@ember/object/computed';
// Internal imports
import ENV from '../../config/environment';
// External imports
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';

export default class SetsController extends Controller {
  // Services
  @service
  fetch;

  @service
  paperToaster;

  @service
  store;

  @service
  toaster;

  // Attributes
  @tracked
  currentSet = null;

  @tracked
  deleteTemplateSetDialogOpen = false;

  @tracked
  entityToDelete = null;

  @tracked
  errorMessage = '';

  @tracked
  saveInProgress = false;

  @tracked
  templateSetDialogOpen = false;

  // Usage sets
  @tracked
  usageSetDialogOpen = false;

  @tracked
  selectedUsageSets = [];

  @tracked
  initialUsageSets = [];

  @setDiff('model.usageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @tracked
  selectedUsageSetsFilter = [];

  @tracked
  applyDialogOpen;

  @tracked
  applyErrorMessage;

  @tracked
  applyInProgress;

  @tracked
  applySet;

  @tracked
  deleteInProgress;

  @tracked
  selectedSchool;

  @action
  setSelectedSchool(value) {
    this.selectedSchool = value;
  }

  @tracked
  templateSetDescription;

  @action
  setTemplateSetDescription(value) {
    this.templateSetDescription = value;
  }

  @tracked
  templateSetId;

  @tracked
  templateSetLabel;

  @action
  setTemplateSetLabel(value) {
    this.templateSetLabel = value;
  }

  @tracked
  deletePageDialogOpen;

  @computed('model.sets.[]', 'selectedUsageSetsFilter.[]')
  get filteredSets() {
    return this.get('model.sets')
      .filter((set) => {
        if (!isEmpty(this.selectedUsageSetsFilter)) {
          return this.selectedUsageSetsFilter.some((usageSet) =>
            set.get('usageSetsArray').includes(usageSet),
          );
        }
        return true;
      })
      .sort(byKeys('label'));
  }

  @action
  addTemplateSet() {
    this.resetForm();
    this.templateSetDialogOpen = true;
  }

  @action
  editTemplateSet(templateSet) {
    this.templateSetId = templateSet.get('id');
    this.templateSetLabel = templateSet.get('label');
    this.templateSetDescription = templateSet.get('description');
    this.templateSetDialogOpen = true;
  }

  @action
  closeTemplateSetDialog() {
    this.templateSetDialogOpen = false;
  }

  @action
  saveTemplateSet() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;

    if (isEmpty(this.templateSetId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('milestones/set', {
          label: this.templateSetLabel,
          description: this.templateSetDescription,
          progressionType: ProgressionType.PROGRESSION,
        });

        resolve(record);
      });
    } else {
      promise = new Promise((resolve) => {
        const id = this.templateSetId;
        this.store.findRecord('milestones/set', id).then((templateSet) => {
          templateSet.set('label', this.templateSetLabel);
          templateSet.set('description', this.templateSetDescription);

          resolve(templateSet);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          this.templateSetDialogOpen = false;
          this.resetForm();
          this.toaster.success('Template Set saved successfully.');
        })
        .catch(() => {
          record.rollbackAttributes();
          this.toaster.error('Error saving Template Set.');
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteTemplateSet(templateSet) {
    this.errorMessage = '';
    this.templateSetLabel = templateSet.get('label');
    this.templateSetId = templateSet.id;
    this.deleteTemplateSetDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    if (confirmed) {
      return new Promise((resolve, reject) => {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let templateSet = this.store.peekRecord(
          'milestones/set',
          this.templateSetId,
        );
        this.entityToDelete = templateSet;
        // Delete record (locally + pushed to server).
        return this.entityToDelete
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            this.entityToDelete.rollbackAttributes();
            reject(err);
          });
      })
        .then(() => {
          // Update the usage sets relationships
          return this.entityToDelete
            .get('usageSetIds')
            .then((usageSetIds) => {
              let promises = [];
              usageSetIds.forEach((usageSetId) => {
                let promise = usageSetId.save();
                promises.push(promise);
              });
              return Promise.all(promises);
            })
            .then(() => {
              this.toaster.success(
                `Template set "${this.entityToDelete.get(
                  'label',
                )}" successfully deleted.`,
              );
              // Tidy up / close dialog.
              this.deletePageDialogOpen = false;
              this.entityToDelete = null;
              this.deleteTemplateSetDialogOpen = false;
              this.resetForm();
            });
        })
        .catch(() => {
          this.toaster.error('Error deleting Template Set.');
        })
        .then(() => {
          this.deleteInProgress = false;
        });
    } else {
      // Cancel the deletion and close the dialog
      this.errorMessage = '';
      this.templateSetLabel = '';
      this.templateSetId = '';
      this.deleteTemplateSetDialogOpen = false;
    }
  }

  // Apply templates
  @action
  openApplyDialog(templateSet) {
    this.applySet = templateSet;
    this.applyErrorMessage = '';
    this.applyInProgress = false;
    this.applyDialogOpen = true;
    this.selectedSchool = null;
  }

  @action
  closeApplyDialog() {
    this.applyDialogOpen = false;
  }

  @action
  applyTemplateSet() {
    let data = {
      schoolId: this.get('selectedSchool.id'),
    };
    let options = {
      data: JSON.stringify(data),
    };

    let templateSetsUri = `${ENV.APP.apiHost}/milestones/${ENV.APP.apiPath}/templates/sets`;
    let setId = this.get('applySet.id');

    this.applyInProgress = true;

    this.fetch
      .post(`${templateSetsUri}/${setId}/:apply`, options)
      .then(() => {
        let toaster = this.paperToaster;
        this.applyErrorMessage = '';
        this.applyInProgress = false;
        this.applyDialogOpen = false;

        let successToast = toaster.show('Template Set applied successfully.', {
          duration: 8000,
          position: 'top right',
          toastClass: 'success',
          action: {
            label: 'Undo',
            onClick: () => {
              toaster.cancelToast(successToast);
              this.fetch
                .post(`${templateSetsUri}/${setId}/:remove`, options)
                .then(() => {
                  toaster.show('Template Set removed successfully.', {
                    duration: 4000,
                    position: 'top right',
                    toastClass: 'success',
                  });
                })
                .catch((err) => {
                  toaster.show(
                    `Error removing Template Set: ${err.errors[0].detail.message}`,
                    {
                      duration: 8000,
                      position: 'top right',
                      toastClass: 'warning',
                    },
                  );
                });
            },
          },
        });
      })
      .catch((err) => {
        this.applyErrorMessage = err.errors[0].detail.message;
        this.applyInProgress = false;
      });
  }

  @action
  openUsageSetDialog(usageSet) {
    usageSet
      .get('usageSetIds')
      .then((usageSetsArray) => {
        this.usageSetDialogOpen = true;
        this.currentSet = usageSet;
        this.selectedUsageSets = usageSetsArray.slice();
        this.initialUsageSets = usageSetsArray.slice();
      })
      .catch(() => {
        this.toaster.error(
          `Error trying to edit the milestone set's usage sets`,
        );
      });
  }

  @action
  closeUsageSetDialog() {
    // The pending save's callbacks still need the state this clears.
    if (this.saveInProgress) {
      return;
    }
    this.usageSetDialogOpen = false;
    this.currentSet = null;
    this.selectedUsageSets = [];
    this.initialUsageSets = [];
  }

  @action
  toggleUsageSet(usageSet, checked) {
    if (checked) {
      this.selectedUsageSets = [...this.selectedUsageSets, usageSet];
    } else {
      this.selectedUsageSets = this.selectedUsageSets.filter(
        (u) => u !== usageSet,
      );
    }
  }

  @action
  saveMilestoneAndUsageSet() {
    this.saveInProgress = true;
    let promises = [];
    let changedUsageSets = [];

    // Update the usage sets
    let removedUsageSets = this.initialUsageSets.filter(
      (usageSet) => !this.selectedUsageSets.includes(usageSet),
    );
    let addedUsageSets = this.selectedUsageSets.filter(
      (usageSet) => !this.initialUsageSets.includes(usageSet),
    );
    changedUsageSets = changedUsageSets.concat(
      removedUsageSets,
      addedUsageSets,
    );
    if (!isEmpty(changedUsageSets)) {
      // Usage sets a page has been newly added to/removed from
      changedUsageSets.forEach((changedUsageSet) => {
        promises.push(changedUsageSet.save());
      });
    }

    // Save the milestone template set
    this.currentSet.set('usageSetIds', this.selectedUsageSets);
    promises.push(this.currentSet.save());

    return Promise.all(promises)
      .then(() => {
        // After saving the page and all involved usage sets
        this.toaster.success(`Successfully saved the page and usage sets`);
        this.usageSetDialogOpen = false;
        this.currentSet = null;
        this.selectedUsageSets = [];
        this.saveInProgress = false;
      })
      .catch(() => {
        this.toaster.error(`Error saving the page's usage sets`);
        // Put the usage sets back, which also reverts each usage set's side.
        // rollbackAttributes() does not touch relationships.
        this.currentSet.set('usageSetIds', this.initialUsageSets);
        this.currentSet.rollbackAttributes();
        this.saveInProgress = false;
      });
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  @action
  toggleMilestoneSetPublished(milestoneSet) {
    let published = milestoneSet.get('isPublished');
    milestoneSet.get('milestones').forEach((milestone) => {
      milestone.set('isPublished', published);
      milestone.save();
    });
    milestoneSet.get('levels').forEach((level) => {
      level.set('isPublished', published);
      level.save();
    });
    milestoneSet.get('expectations').forEach((expectation) => {
      expectation.set('isPublished', published);
      expectation.save();
    });
    // Unpublish removes the set from all usage sets, so it becomes unusable.
    if (published) {
      return;
    }
    milestoneSet.get('usageSetIds').then((usageSetsArray) => {
      this.selectedUsageSets = [];
      this.initialUsageSets = usageSetsArray.slice();
      this.currentSet = milestoneSet;
      this.saveMilestoneAndUsageSet();
    });
  }

  resetForm() {
    this.templateSetId = null;
    this.templateSetLabel = '';
    this.templateSetDescription = '';
  }

  reset() {
    this.resetForm();
    this.selectedUsageSetsFilter = [];
  }
}
